import type ApplicationModel from 'components/framework/ApplicationModel';
import { capitalize } from 'lodash';

const stylize = (replacement: string, original: string) => {
  if (/^([A-Z])+$/.test(original)) {
    return replacement.toUpperCase().trim();
  } else if (/^([A-Z])+$/.test(original.charAt(0))) {
    return capitalize(replacement).trim();
  } else {
    return replacement;
  }
};

interface TerminologyOptions {
  // Record count, used to calculate isPlural
  count?: number;
  // Request the plural form
  isPlural?: boolean;
  // Lowercase value before conversion
  lowercase?: boolean;
}

export default function terminology(
  value: string,
  { count, isPlural = undefined, lowercase = false }: TerminologyOptions = {}
) {
  if (value == null) value = '';

  if (typeof count === 'number') {
    isPlural = count !== 1;
  } else if (isPlural === undefined) {
    isPlural = value.charAt(value.length - 1).toLowerCase() === 's';
  }

  if (lowercase) {
    value = value.toLowerCase();
  }

  const key = value
    .replace(/(\S+)([A-Z]+)/g, '$1_$2') // Add underscores between camelCase
    .toLowerCase()
    .replace(/s$/, '') // Remove trailing 's' (plural)
    .replace(/s\s/, ' ') // Replace 's ' with ' ' (inline plurals)
    .replace(/\s/g, '_'); // Replace spaces with underscores

  const translation = window.terminologyLookup?.[key];
  if (translation == null) {
    return value;
  } else {
    const text = isPlural ? translation.plural : translation.normal;
    return text ? stylize(text, value) : value;
  }
}

export function hiddenAssociationText(
  recordType: string,
  opts: TerminologyOptions = {}
) {
  return `[Hidden ${terminology(recordType, opts)}]`;
}

interface TerminologyNameProvider {
  terminologyName?: string;
  modelConstructor?: { terminologyName?: string };
  typename?: string;
}

function terminologyName(object: TerminologyNameProvider): string | undefined {
  if (typeof object.terminologyName === 'string') {
    return object.terminologyName;
  }
}

/**
 * Given a model it uses the model's `terminologyName` method, falling back to
 * the model class `terminologyName` and then finally falling back to the model
 * `typename`
 *
 * ```
 * const text = modelTerminology(new Feature()); // => 'Feature'
 * ```
 */
export function modelTerminology(
  model: ApplicationModel,
  options: TerminologyOptions = {}
) {
  const name =
    terminologyName(model) ||
    terminologyName(model.modelConstructor) ||
    model.typename;
  return terminology(name, options);
}
